import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { produce, type Draft } from 'immer';

export type { Draft } from 'immer';

export interface ViewModel<T> {
  state: Omit<BehaviorSubject<T>, 'next'>;
  dispose(): void;
}

export abstract class AbstractViewModel<T> implements ViewModel<T> {
  state: BehaviorSubject<T>;

  private subscription: Subscription;

  private readonly _initialState: T;

  get initialState(): T {
    return { ...this._initialState };
  }

  protected constructor(initialState: T) {
    this._initialState = initialState;
    this.state = new BehaviorSubject(this._initialState);
    this.subscription = new Subscription();

    if (import.meta.env.VITE_DEPLOY_ENV === 'dev') {
      (window as any).currentViewModel = this;
    }
  }

  /**
   * Update state using Immer - mutate the state directly.
   * @example
   * this.update((state) => {
   *   state.loading = true;
   *   state.user.profile.name = 'New Name';
   * });
   */
  update(recipe: (state: Draft<T>) => void) {
    this.state.next(produce(this.state.getValue(), recipe));
  }

  subscribe<O>(observable: Observable<O>) {
    this.subscription.add(observable.subscribe());
  }

  reset(): void {
    this.state.next(this.initialState);
  }

  dispose(): void {
    this.subscription.unsubscribe();
  }
}
